/**
 * THE PERMISSION MATRIX.
 *
 * Direct transcription of the table in docs/phase0/04-permissions-matrix.md.
 * Keep the two in sync — `matrix.test.ts` asserts structural invariants, but it
 * cannot know what the business intended, so a change here MUST be mirrored in
 * the markdown and recorded in the decision log if it changes a role's rights.
 *
 * Notation, matching the doc:
 *   V view · C create · E edit · D delete/archive · A approve · X export · M admin
 *   g(...)          grants over all records on the project
 *   scoped(..., s)  grants limited to scope s ('own' | 'assigned' | ...)
 *   fin(...)        grants additionally requiring the finance:view flag  (matrix `V*`)
 *   (absent key)    no access
 */

import { g, scoped, fin, merge, type PermissionMatrix } from './types.js';

export const PERMISSION_MATRIX: PermissionMatrix = Object.freeze({
  // -------------------------------------------------------------------------
  company_admin: Object.freeze({
    company: g('VEM'),
    user: g('VCEDM'),
    role: g('VCEDM'),
    template: g('VCED'),
    project: g('VCEDX'),
    stage: g('VE'),
    milestone: g('VCED'),
    location: g('VCED'),
    team: g('VCED'),
    task: g('VCEDX'),
    deliverable: g('VCEDX'),
    submittal: g('VCEDX'),
    material: g('VCEDX'),
    supplier: g('VCED'),
    delivery: g('VCED'),
    activity: g('VCEDX'),
    diary: g('VCEDX'),
    photo: g('VCEDX'),
    inspection: g('VCEDX'),
    ncr: g('VCEDX'),
    snag: g('VCEDX'),
    handover: g('VCED'),
    budget: g('VX'),
    cost: g('VX'),
    /*
     * `E` ADDED 2026-08-25, by the user's decision: *"let the Admin work on
     * them too"*.
     *
     * `05` names `company_admin` on all twelve variation transitions and `04`
     * gave them view and export, so every one of those buttons was drawn and
     * refused (ADR-039). On a small team the admin IS the person chasing
     * variations, and the alternative — removing twelve buttons — would have
     * been resolving it the way that was easier to code rather than the way the
     * business works.
     *
     * `C` ADDED 2026-09-03, and it is the same decision finished. Edit without
     * create left an admin who could move a variation through all twelve of its
     * transitions and could not raise one — so the register offered him no
     * button at all and told him to go and find the QS. The owner hit exactly
     * that on his own project. If the admin is the person chasing variations,
     * he is the person who writes down the change the client asked for on the
     * phone.
     *
     * Approve is still NOT here, and that is the line. `A` belongs to the
     * Director and the QS: raising a change is bookkeeping, agreeing to be paid
     * for it is a commercial decision, and the two should not be the same
     * person by default.
     */
    variation: g('VCEX'),
    /*
     * `C` AND `E` ADDED 2026-09-03, by the owner's decision: *"give it to the
     * admin"*.
     *
     * Invoicing had always been the QS's alone (`VCEDX`), and that is a real
     * segregation-of-duties argument rather than an oversight — a stronger one
     * than raising a variation. It was put to the owner as a question and this
     * is his answer: on a firm this size he is the person who invoices.
     *
     * BOTH, not just create, and the routes are why: `create` raises an invoice
     * AND records a receipt, while `edit` is what ISSUES or cancels one. Create
     * alone would leave an admin able to raise an invoice and unable to issue
     * it — exactly the half-a-decision that `variation: E` without `C` left
     * behind for a fortnight.
     *
     * `D` stays with the QS. Destroying a financial record is not the same act
     * as writing one, and nothing in the product asks for it: no billing route
     * demands `delete`.
     */
    billing: g('VCEX'),
    document: g('VCEDX'),
    report: g('VCEX'),
    pulse: g('V'),
    audit: g('VX'),
    ai: g('VM'),
  }),

  // -------------------------------------------------------------------------
  director: Object.freeze({
    company: g('V'),
    user: g('V'),
    role: g('V'),
    template: g('V'),
    project: g('VCEX'),
    stage: g('VEA'),
    milestone: g('VCE'),
    location: g('V'),
    team: g('VCE'),
    task: g('VCEX'),
    deliverable: g('VX'),
    submittal: g('VAX'),
    material: g('VX'),
    supplier: g('V'),
    delivery: g('V'),
    activity: g('VX'),
    diary: g('VX'),
    photo: g('VX'),
    inspection: g('VX'),
    ncr: g('VX'),
    snag: g('VX'),
    handover: g('VA'),
    budget: g('VX'),
    cost: g('VX'),
    variation: g('VAX'),
    billing: g('VX'),
    document: g('VX'),
    report: g('VX'),
    pulse: g('V'),
    audit: g('V'),
    ai: g('V'),
  }),

  // -------------------------------------------------------------------------
  project_manager: Object.freeze({
    template: g('V'),
    project: g('VCEX'),
    stage: g('VEA'),
    milestone: g('VCED'),
    location: g('VCED'),
    team: g('VCED'),
    task: g('VCEDX'),
    deliverable: g('VCEX'),
    submittal: g('VCEAX'),
    material: g('VCEX'),
    supplier: g('V'),
    delivery: g('VCE'),
    activity: g('VCEDX'),
    diary: g('VCEX'),
    photo: g('VCEDX'),
    inspection: g('VCEX'),
    ncr: g('VCEX'),
    snag: g('VCEDX'),
    handover: g('VCEA'),
    // Financial visibility for a PM is grantable but revocable per project.
    budget: fin('V'),
    cost: fin('V'),
    variation: g('VCE'),
    billing: fin('V'),
    document: g('VCEDX'),
    report: g('VCEAX'),
    pulse: merge(g('V'), scoped('E', 'override')),
    audit: scoped('V', 'project'),
    ai: g('V'),
  }),

  // -------------------------------------------------------------------------
  design_coordinator: Object.freeze({
    template: g('V'),
    project: g('V'),
    stage: g('V'),
    milestone: g('V'),
    location: g('V'),
    team: g('V'),
    task: g('VCE'),
    deliverable: g('VCEDX'),
    submittal: g('VCEX'),
    material: g('V'),
    activity: g('V'),
    diary: g('V'),
    photo: g('VC'),
    inspection: g('V'),
    /*
     * `E` ADDED 2026-08-25, by the user's decision: *"yes but with proof"*.
     *
     * `05` names this role on `corrective_action_assigned → work_complete` —
     * the person who was told to put something right saying they have — and
     * `04` gave them view only, so the button was drawn and refused (ADR-039).
     *
     * THE PROOF WAS ALREADY THE RULE. That transition is marked
     * `requiresEvidence`, and `moveNcr` refuses it without a completion note
     * saying what was actually done. So this grants the right to report the
     * work, not the right to wave it through.
     *
     * It also closes nothing: only QA/QC can move `work_complete → verified`,
     * and ADR-020 keeps the person who did the work off their own verification.
     * `ncr: edit` opens exactly one endpoint — the status route — because
     * there is no NCR content-edit route to open.
     */
    ncr: g('VE'),
    snag: g('V'),
    handover: g('V'),
    document: g('VCE'),
    report: g('V'),
    pulse: g('V'),
    ai: g('V'),
  }),

  // -------------------------------------------------------------------------
  procurement: Object.freeze({
    template: g('V'),
    project: g('V'),
    stage: g('V'),
    milestone: g('V'),
    location: g('V'),
    team: g('V'),
    task: g('VCE'),
    deliverable: g('V'),
    submittal: g('V'),
    material: g('VCEDX'),
    supplier: g('VCED'),
    delivery: g('VCE'),
    activity: g('V'),
    diary: g('V'),
    photo: g('VC'),
    inspection: g('V'),
    /*
     * `E` ADDED 2026-08-25, by the user's decision: *"yes but with proof"*.
     *
     * `05` names this role on `corrective_action_assigned → work_complete` —
     * the person who was told to put something right saying they have — and
     * `04` gave them view only, so the button was drawn and refused (ADR-039).
     *
     * THE PROOF WAS ALREADY THE RULE. That transition is marked
     * `requiresEvidence`, and `moveNcr` refuses it without a completion note
     * saying what was actually done. So this grants the right to report the
     * work, not the right to wave it through.
     *
     * It also closes nothing: only QA/QC can move `work_complete → verified`,
     * and ADR-020 keeps the person who did the work off their own verification.
     * `ncr: edit` opens exactly one endpoint — the status route — because
     * there is no NCR content-edit route to open.
     */
    ncr: g('VE'),
    snag: g('V'),
    handover: g('V'),
    cost: fin('V'),
    document: g('VCE'),
    report: g('V'),
    pulse: g('V'),
    ai: g('V'),
  }),

  // -------------------------------------------------------------------------
  site_engineer: Object.freeze({
    project: g('V'),
    stage: g('V'),
    milestone: g('V'),
    location: g('V'),
    team: g('V'),
    task: scoped('VCE', 'own'),
    deliverable: g('V'),
    submittal: g('V'),
    material: g('V'),
    delivery: g('VC'),
    activity: g('VCE'),
    diary: g('VCE'),
    photo: g('VC'),
    inspection: g('VC'),
    /*
     * `E` ADDED 2026-08-25, by the user's decision: *"yes but with proof"*.
     *
     * `05` names this role on `corrective_action_assigned → work_complete` —
     * the person who was told to put something right saying they have — and
     * `04` gave them view only, so the button was drawn and refused (ADR-039).
     *
     * THE PROOF WAS ALREADY THE RULE. That transition is marked
     * `requiresEvidence`, and `moveNcr` refuses it without a completion note
     * saying what was actually done. So this grants the right to report the
     * work, not the right to wave it through.
     *
     * It also closes nothing: only QA/QC can move `work_complete → verified`,
     * and ADR-020 keeps the person who did the work off their own verification.
     * `ncr: edit` opens exactly one endpoint — the status route — because
     * there is no NCR content-edit route to open.
     */
    ncr: g('VE'),
    snag: g('VCE'),
    handover: g('V'),
    variation: g('VC'),
    document: g('VC'),
    report: g('V'),
    pulse: g('V'),
    ai: g('V'),
  }),

  // -------------------------------------------------------------------------
  qs_commercial: Object.freeze({
    template: g('V'),
    project: g('VX'),
    stage: g('V'),
    milestone: g('V'),
    location: g('V'),
    team: g('V'),
    task: g('VCE'),
    material: g('V'),
    supplier: g('V'),
    activity: g('V'),
    diary: g('V'),
    photo: g('V'),
    handover: g('V'),
    budget: g('VCEDX'),
    cost: g('VCEDX'),
    variation: g('VCEAX'),
    billing: g('VCEDX'),
    document: g('VCE'),
    report: g('VX'),
    pulse: g('V'),
    ai: g('V'),
    ncr: g('V'),
  }),

  // -------------------------------------------------------------------------
  qa_qc: Object.freeze({
    template: g('V'),
    project: g('V'),
    stage: g('V'),
    milestone: g('V'),
    location: g('V'),
    team: g('V'),
    task: g('VCE'),
    deliverable: g('V'),
    submittal: g('V'),
    material: g('V'),
    delivery: g('V'),
    activity: g('V'),
    diary: g('V'),
    photo: g('VC'),
    inspection: g('VCEAX'),
    ncr: g('VCEAX'),
    snag: g('VCEAX'),
    handover: g('V'),
    document: g('VCE'),
    report: g('V'),
    pulse: g('V'),
    ai: g('V'),
  }),

  // -------------------------------------------------------------------------
  // EXTERNAL — additionally constrained by the hard rules in `04`, enforced
  // independently of this matrix. Never holds finance:view (role-level rule).
  // -------------------------------------------------------------------------
  client_consultant: Object.freeze({
    project: g('V'),
    milestone: g('V'),
    location: g('V'),
    deliverable: g('V'),
    submittal: g('VA'),
    inspection: g('V'),
    ncr: g('V'),
    /*
     * `A` ADDED 2026-08-25, by the user's decision: *"if there is a client
     * account yes, if not the pm will do"*.
     *
     * `05` has always named `client_consultant` on `open → accepted_as_is` and
     * `assigned → accepted_as_is` — the honest state for "the client agreed to
     * live with it" — and `04` gave them no right that reaches it. So the
     * register drew the button and the endpoint answered 403. Found by driving
     * a real project through the API (ADR-039).
     *
     * `A`, NOT `E`, and that difference is the point: `snag: edit` also opens
     * `PATCH /snags/:id`, which is the description, the severity and the
     * assignee. A client agreeing to live with a defect must not be able to
     * rewrite the record of it. Accepting as-is IS an approval, so `approve` is
     * both the honest name for it and the narrow one — the machine allows them
     * nothing else, and a reason is mandatory.
     *
     * On a job with no client account nothing changes: the PM records it, as
     * they always could.
     */
    snag: g('VCA'),
    handover: g('VA'),
    /*
     * VIEW ONLY, by the user's decision on 2026-08-17: *"no need for client
     * approval from the app"*.
     *
     * `04` gave `VA` here and the ROUTE has always been guarded by
     * `variation:edit`, so the approve half was never reachable — the client's
     * screen offered Approve and Reject buttons that answered 403. That was the
     * inconsistency; this resolves it in the direction the user chose rather
     * than the one that was easier to code.
     *
     * A variation is now agreed OUTSIDE the app — an email, a signed
     * instruction — and the QS records it, exactly as an instruction given
     * verbally on site is recorded. The STATE MACHINE agrees: `client_consultant`
     * is off the approve and reject transitions, so no button is offered
     * either, and the two halves cannot drift.
     *
     * SUBMITTALS ARE UNTOUCHED. A consultant still decides those in the app;
     * that is the whole point of that module, and it is a different question
     * from who signs off a change in price.
     */
    variation: g('V'),
    document: scoped('V', 'shared'),
    report: scoped('V', 'issued'),
    photo: g('V'),
  }),

  // -------------------------------------------------------------------------
  subcontractor: Object.freeze({
    project: g('V'),
    location: g('V'),
    task: scoped('VE', 'assigned'),
    deliverable: scoped('V', 'assigned'),
    submittal: scoped('VC', 'assigned'),
    activity: scoped('V', 'assigned'),
    photo: scoped('VC', 'assigned'),
    inspection: scoped('V', 'assigned'),
    ncr: scoped('VE', 'assigned'),
    snag: scoped('VE', 'assigned'),
    // `assigned`, not `shared` (ADR-047, 2026-09-04): the files on THEIR
    // records — attached to something in their packages or given to them by
    // name, or uploaded by them. "Shared with the client" is the client's
    // rule, and the owner said a trade should not read client correspondence.
    document: scoped('V', 'assigned'),
  }),
});
